import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, PiggyBank, RefreshCw, Search } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import {
  savingsReconciliationApi,
  SavingsAccountPage,
  SavingsAccountRow,
  SavingsCheckResult,
  SavingsPerson,
  SavingsReconciliation as Reconciliation,
  StuckSavingsMove,
} from '../../api/savings-reconciliation';
import { isApprovalRequired } from '../../api/approvals';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';

/**
 * Savings accounts against what their goals say they hold.
 *
 * Every person's savings live in a Safe Haven account in their own name, and
 * each goal's balance is a share of it. The two should always agree; this
 * page is where somebody finds out when they don't, and where the moves the
 * bank never answered for are settled.
 *
 * The list reads only PayIQ's tables, so it stays fast. The bank is asked
 * when somebody opens an account, or runs "Check all", and never on page
 * load — a provider call per row is how a list gets slow and Safe Haven
 * starts refusing.
 *
 * Nothing here edits a balance. Resolving a stuck move does credit or
 * refund a goal, which is why it goes to a second admin.
 */

const naira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

const sectionTitle: React.CSSProperties = {
  margin: '0 0 6px',
  fontSize: 15,
  color: 'var(--neutral-900)',
};

const sectionNote: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 13,
  color: 'var(--neutral-600)',
  maxWidth: 680,
};

/** Bank less goals, as a badge: matched, short (worse) or over. */
const DifferenceBadge: React.FC<{ difference: number | null }> = ({ difference }) => {
  if (difference === null) return <Badge variant="warning">Bank didn&apos;t answer</Badge>;
  if (Math.abs(difference) < 0.01) return <Badge variant="success">Matches</Badge>;
  // Short is the one that matters: goals promising money the account
  // doesn't hold. Over is money nobody's goal accounts for.
  return difference < 0 ? (
    <Badge variant="danger">Short {naira(Math.abs(difference))}</Badge>
  ) : (
    <Badge variant="info">Over {naira(difference)}</Badge>
  );
};

const PersonCell: React.FC<{ person: SavingsPerson | null; userId: string }> = ({
  person,
  userId,
}) => (
  <>
    <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
      {person?.name ?? 'Unnamed'}
    </div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
      {person?.email ?? person?.phone ?? userId}
    </div>
  </>
);

export const SavingsReconciliation: React.FC = () => {
  usePage('Savings Reconciliation');
  const toast = useToast();
  const { can } = useAuth();

  const [accounts, setAccounts] = useState<SavingsAccountPage | null>(null);
  const [stuck, setStuck] = useState<StuckSavingsMove[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<SavingsCheckResult | null>(null);

  const [detail, setDetail] = useState<{
    userId: string;
    person: SavingsPerson | null;
    data: Reconciliation | null;
  } | null>(null);

  const [resolving, setResolving] = useState<{
    move: StuckSavingsMove;
    outcome: 'completed' | 'failed';
  } | null>(null);
  const [resolveBusy, setResolveBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, stuckMoves] = await Promise.all([
        savingsReconciliationApi.accounts({ q: search || undefined, page, limit: 25 }),
        savingsReconciliationApi.stuck(),
      ]);
      setAccounts(list);
      setStuck(stuckMoves);
    } catch (err) {
      toast.error(err, 'Failed to load savings accounts.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function checkAll() {
    try {
      setChecking(true);
      const result = await savingsReconciliationApi.checkAll();
      setCheck(result);
      toast.success(
        `Checked ${result.checked} account${result.checked === 1 ? '' : 's'} — ` +
          `${result.mismatched} not matching, ${result.unreachable} unreachable.`,
      );
    } catch (err) {
      toast.error(err, 'Could not check the accounts.');
    } finally {
      setChecking(false);
    }
  }

  async function openDetail(userId: string, person: SavingsPerson | null) {
    setDetail({ userId, person, data: null });
    try {
      const data = await savingsReconciliationApi.reconcile(userId);
      setDetail({ userId, person, data });
    } catch (err) {
      toast.error(err, 'Could not read this savings account.');
      setDetail(null);
    }
  }

  async function confirmResolve() {
    if (!resolving) return;
    setResolveBusy(true);
    try {
      const result = await savingsReconciliationApi.resolve(
        resolving.move.id,
        resolving.outcome,
      );
      if (isApprovalRequired(result)) {
        toast.success('Sent for a second admin to approve.', {
          to: '/approvals',
          label: 'View Approvals',
        });
      } else {
        toast.success(
          resolving.outcome === 'completed'
            ? 'Recorded as gone through. The goal is up to date.'
            : 'Recorded as not gone through. The goal is up to date.',
        );
      }
      setResolving(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not resolve that move.');
    } finally {
      setResolveBusy(false);
    }
  }

  const canResolve = can('savings_reconciliation.resolve');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)', maxWidth: 680 }}>
            Each person&apos;s savings account at Safe Haven, beside what their
            goals say it holds. The two should always match. A move still in
            flight can open a gap for a minute or so. Anything else is a
            question for a person: nothing on this page edits a balance.
          </p>
          <Button variant="secondary" onClick={() => void checkAll()} disabled={checking}>
            <RefreshCw size={15} style={{ marginRight: 6 }} />
            {checking ? 'Checking…' : 'Check all against the bank'}
          </Button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          <StatCard
            label="Savings accounts"
            value={accounts?.meta.total ?? '—'}
            icon={PiggyBank}
            accent="blue"
          />
          <StatCard
            label="Moves stuck over 30 min"
            value={stuck.length}
            icon={Clock}
            accent={stuck.length > 0 ? 'purple' : 'neutral'}
          />
          {check && (
            <>
              <StatCard
                label="Not matching the bank"
                value={check.mismatched}
                icon={AlertTriangle}
                accent={check.mismatched > 0 ? 'purple' : 'neutral'}
              />
              <StatCard label="Bank didn't answer" value={check.unreachable} accent="neutral" />
            </>
          )}
        </div>
      </Card>

      {check && (
        <Card padding={20}>
          <h3 style={sectionTitle}>Last check against the bank</h3>
          <p style={sectionNote}>
            {check.checked} account{check.checked === 1 ? '' : 's'} checked
            {check.capped ? ' (the first 1,000 only)' : ''}. Only the ones that
            don&apos;t match, or that the bank didn&apos;t answer for, are listed.
          </p>
          {check.findings.length === 0 ? (
            <EmptyState title="Every savings account matches its goals." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Person', 'Bank', 'Goals', 'Difference', 'In flight', ''].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {check.findings.map((f) => (
                    <tr key={f.userId} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <PersonCell person={f.person} userId={f.userId} />
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {f.bankBalance === null ? '—' : naira(f.bankBalance)}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(f.goalsTotal)}</td>
                      <td style={td}>
                        <DifferenceBadge difference={f.difference} />
                      </td>
                      <td style={td}>{f.inFlight.length || '—'}</td>
                      <td style={td}>
                        <Button variant="ghost" onClick={() => void openDetail(f.userId, f.person)}>
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {stuck.length > 0 && (
        <Card padding={20}>
          <h3 style={sectionTitle}>Moves the bank never answered for</h3>
          <p style={sectionNote}>
            Sent over 30 minutes ago and still unconfirmed. They&apos;re settled
            automatically as soon as Safe Haven gives an answer; these are the
            ones it hasn&apos;t. Check the statement, then say what happened.
            &ldquo;Went through&rdquo; credits a deposit to its goal.
            &ldquo;Didn&apos;t&rdquo; gives a withdrawal back to its goal.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Person', 'Goal', 'Move', 'Amount', 'Reference', 'Sent', ''].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stuck.map((m) => (
                  <tr key={m.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <PersonCell person={m.person} userId={m.userId} />
                    </td>
                    <td style={td}>{m.goalName ?? '—'}</td>
                    <td style={td}>
                      <Badge variant={m.direction === 'in' ? 'info' : 'neutral'}>
                        {m.direction === 'in' ? 'Into savings' : 'Out of savings'}
                      </Badge>
                    </td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{naira(m.amount)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{m.reference}</td>
                    <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                      {dateTime(m.createdAt)}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canResolve ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            variant="secondary"
                            onClick={() => setResolving({ move: m, outcome: 'completed' })}
                          >
                            Went through
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setResolving({ move: m, outcome: 'failed' })}
                          >
                            Didn&apos;t
                          </Button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          Needs resolve access
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card padding={20}>
        <h3 style={sectionTitle}>Savings accounts</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(query.trim());
          }}
          style={{ display: 'flex', gap: 8, margin: '12px 0 20px', maxWidth: 480 }}
        >
          <div style={{ position: 'relative', flex: 1 }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--neutral-500)',
              }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email, phone or user id"
              style={{
                width: '100%',
                height: 38,
                padding: '0 12px 0 34px',
                fontSize: 13,
                fontFamily: 'var(--font-family)',
                color: 'var(--neutral-800)',
                background: 'var(--neutral-100)',
                border: '1px solid var(--neutral-300)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !accounts?.items.length ? (
          <EmptyState
            title={search ? 'No savings accounts match that search.' : 'No savings accounts yet.'}
            description={
              search
                ? undefined
                : 'One is opened the first time somebody creates a goal or turns on Spend & Save.'
            }
          />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Person', 'Account', 'Goals hold', 'In flight', 'Opened', ''].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.items.map((a: SavingsAccountRow) => (
                    <tr key={a.userId} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <PersonCell person={a.person} userId={a.userId} />
                      </td>
                      <td style={td}>
                        <div style={{ fontFamily: 'monospace' }}>{a.account}</div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          {a.accountName ?? '—'}
                        </div>
                      </td>
                      <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {naira(a.goalsTotal)}
                      </td>
                      <td style={td}>
                        {a.inFlight > 0 ? <Badge variant="info">{a.inFlight}</Badge> : '—'}
                      </td>
                      <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                        {dateTime(a.openedAt)}
                      </td>
                      <td style={td}>
                        <Button variant="ghost" onClick={() => void openDetail(a.userId, a.person)}>
                          Check bank
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 16 }}>
              <Pagination
                page={accounts.meta.page}
                totalPages={accounts.meta.totalPages}
                total={accounts.meta.total}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </Card>

      <Drawer
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.person?.name ?? 'Savings account'}
        subtitle={detail?.data?.account?.name ?? detail?.person?.email ?? undefined}
      >
        {!detail?.data ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
              }}
            >
              <StatCard
                label="Bank holds"
                value={detail.data.bankBalance === null ? '—' : naira(detail.data.bankBalance)}
                accent="blue"
              />
              <StatCard label="Goals say" value={naira(detail.data.goalsTotal)} accent="neutral" />
            </div>
            <div>
              <DifferenceBadge difference={detail.data.difference} />
              {detail.data.inFlight.length > 0 && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--neutral-600)' }}>
                  {detail.data.inFlight.length} move
                  {detail.data.inFlight.length === 1 ? '' : 's'} still in flight can
                  explain a gap until the bank confirms.
                </p>
              )}
            </div>

            <div>
              <h4 style={{ ...sectionTitle, fontSize: 13 }}>Goals</h4>
              {detail.data.goals.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>No goals.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {detail.data.goals.map((g) => (
                      <tr key={g.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{g.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                            {g.type.replace('_', ' ')} · {g.status}
                          </div>
                        </td>
                        <td
                          style={{
                            ...td,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            color: g.status === 'cancelled' ? 'var(--neutral-500)' : undefined,
                          }}
                        >
                          {naira(g.savedAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {detail.data.inFlight.length > 0 && (
              <div>
                <h4 style={{ ...sectionTitle, fontSize: 13 }}>In flight</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {detail.data.inFlight.map((m) => (
                      <tr key={m.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                        <td style={td}>{m.direction === 'in' ? 'Into savings' : 'Out of savings'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(m.amount)}</td>
                        <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                          {dateTime(m.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={resolving !== null}
        title={resolving?.outcome === 'completed' ? 'It went through' : "It didn't go through"}
        message={
          resolving
            ? resolving.outcome === 'completed'
              ? resolving.move.direction === 'in'
                ? `The statement shows ${naira(resolving.move.amount)} arrived in savings. Its goal will be credited.`
                : `The statement shows ${naira(resolving.move.amount)} reached the main account. The goal was already debited; nothing else changes.`
              : resolving.move.direction === 'in'
                ? `The statement shows ${naira(resolving.move.amount)} never left the main account. Nothing is credited.`
                : `The statement shows ${naira(resolving.move.amount)} never left savings. It goes back into its goal.`
            : ''
        }
        confirmLabel="Record it"
        loading={resolveBusy}
        onConfirm={() => void confirmResolve()}
        onCancel={() => setResolving(null)}
      />
    </div>
  );
};
